import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ScopeCatalogEntry, User } from '@sss/sdk';
import LoginForm from '../components/Auth/LoginForm';
import { sdk } from '../lib/sdk';
import {
  SWITCH_ACCOUNT_KEY,
  approveInteraction,
  bindInteraction,
  denyInteraction,
  getInteraction,
  type InteractionDetails,
} from '../lib/oauthInteraction';
import { clearSession } from '../store/authSlice';
import type { RootState } from '../store';

/**
 * `/authorize?interaction=<uid>` — where the API's OAuth Authorization Server
 * sends the browser to sign in and approve an app (e.g. an MCP client).
 *
 *  1. Not signed in → inline sign-in (with MFA).
 *  2. Login prompt → account chooser: "Continue as X" binds the request to
 *     this account; "Use a different account" signs out, signs in again and
 *     rebinds (a sessionStorage flag carries the intent across the re-login).
 *  3. Consent prompt → who is asking (flagged if self-registered), where the
 *     user will be sent, and exactly which permissions; Allow / Deny.
 * Every step ends by navigating to the `redirectTo` the API returns.
 */
export default function AuthorizePage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const uid = params.get('interaction') ?? '';
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const [me, setMe] = useState<User | null>(null);
  const [details, setDetails] = useState<InteractionDetails | null>(null);
  const [catalog, setCatalog] = useState<ScopeCatalogEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const go = (redirectTo: string) => window.location.assign(redirectTo);

  const run = useCallback(async (step: () => Promise<{ redirectTo: string }>) => {
    setBusy(true);
    setError(null);
    try {
      go((await step()).redirectTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
      setBusy(false);
    }
  }, []);

  // Load who's signed in, the interaction, and the scope labels.
  useEffect(() => {
    if (!accessToken || !uid) return;
    let cancelled = false;
    Promise.all([sdk.users.me(), getInteraction(uid), sdk.config.get()]).then(
      ([user, interaction, config]) => {
        if (cancelled) return;
        setMe(user);
        setDetails(interaction);
        setCatalog(config.tokenScopes);
        // Returning from "Use a different account": rebind straight away.
        if (interaction.prompt === 'login' && sessionStorage.getItem(SWITCH_ACCOUNT_KEY) === uid) {
          sessionStorage.removeItem(SWITCH_ACCOUNT_KEY);
          run(() => bindInteraction(uid));
        }
      },
      (err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t('authorize.expired'));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [accessToken, uid, run, t]);

  const useDifferentAccount = () => {
    sessionStorage.setItem(SWITCH_ACCOUNT_KEY, uid);
    setMe(null);
    setDetails(null);
    dispatch(clearSession());
  };

  const clientLabel = details?.clientName || t('authorize.unknownApp');
  const redirectHost = (() => {
    try {
      return details?.redirectUri ? new URL(details.redirectUri).host || details.redirectUri : null;
    } catch {
      return details?.redirectUri ?? null;
    }
  })();
  const describeScope = (scope: string): string => {
    if (scope === 'openid') return t('authorize.identity');
    if (scope === 'offline_access') return t('authorize.offlineAccess');
    const entry = catalog.find((c) => c.name === scope);
    return entry ? `${entry.label} — ${entry.description}` : scope;
  };

  if (!uid) return <Shell title={t('authorize.title')}><p>{t('authorize.expired')}</p></Shell>;

  if (!accessToken) {
    return (
      <Shell title={t('authorize.title')}>
        <p>{t('authorize.signInToContinue')}</p>
        <LoginForm onSignedIn={() => undefined} />
      </Shell>
    );
  }

  if (error && !details) return <Shell title={t('authorize.title')}><p style={{ color: 'red' }}>{error}</p></Shell>;
  if (!details || !me) return <Shell title={t('authorize.title')}><p>{t('common.loading')}</p></Shell>;

  const meLabel = me.displayName || me.email;

  if (details.prompt === 'login') {
    return (
      <Shell title={t('authorize.wantsAccess', { client: clientLabel })}>
        <button type="button" disabled={busy} onClick={() => run(() => bindInteraction(uid))} style={primary}>
          {t('authorize.continueAs', { name: meLabel })}
        </button>
        <button type="button" disabled={busy} onClick={useDifferentAccount} style={secondary}>
          {t('authorize.useDifferentAccount')}
        </button>
        {error && <p style={{ color: 'red' }}>{error}</p>}
      </Shell>
    );
  }

  // Consent — but only from the account the request was bound to.
  if (details.accountId && details.accountId !== me.id) {
    return (
      <Shell title={t('authorize.title')}>
        <button type="button" onClick={useDifferentAccount} style={secondary}>
          {t('authorize.useDifferentAccount')}
        </button>
      </Shell>
    );
  }

  return (
    <Shell title={t('authorize.wantsAccess', { client: clientLabel })}>
      {!details.verified && <p style={warning}>{t('authorize.unverifiedWarning')}</p>}
      <p style={{ color: '#555' }}>{t('authorize.continueAs', { name: meLabel })}</p>
      <p>{t('authorize.permissions')}</p>
      <ul>
        {details.scopes.map((scope) => (
          <li key={scope}>{describeScope(scope)}</li>
        ))}
      </ul>
      {redirectHost && <p style={{ color: '#555' }}>{t('authorize.redirectTo', { host: redirectHost })}</p>}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button type="button" disabled={busy} onClick={() => run(() => approveInteraction(uid))} style={primary}>
          {t('authorize.allow')}
        </button>
        <button type="button" disabled={busy} onClick={() => run(() => denyInteraction(uid))} style={secondary}>
          {t('authorize.deny')}
        </button>
      </div>
      {error && <p style={{ color: 'red' }}>{error}</p>}
    </Shell>
  );
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main style={{ maxWidth: 420, margin: '4rem auto', padding: '0 1rem', fontFamily: 'system-ui, sans-serif', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <h1 style={{ fontSize: '1.4rem' }}>{title}</h1>
      {children}
    </main>
  );
}

const primary: React.CSSProperties = { padding: '0.6rem 1rem', cursor: 'pointer', fontWeight: 600 };
const secondary: React.CSSProperties = { padding: '0.6rem 1rem', cursor: 'pointer', background: 'none' };
const warning: React.CSSProperties = { background: '#fff4e5', border: '1px solid #f0b46a', borderRadius: 4, padding: '0.5rem 0.75rem' };
